/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable no-shadow */
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { v4 as uuidv4 } from "uuid";
import { faCog, faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import Input from "./Input";
import {
  addNewInputArgument,
  selectCounterCategories,
  selectDataCategories,
  selectInputArguments,
  selectRawDataCategories,
} from "../../../../store/slices/projectSlice";
import InputLabel from "./InputLabel";
import { setCurrentModal } from "../../../../store/slices/modalsSlice";
import InputGroupText from "react-bootstrap/esm/InputGroupText";

const StyledInput = styled.div`
  & .nav {
    border-bottom: none;
  }
  & .nav-link {
    font-size: 0.75rem;
    padding: 0.2rem 0.5rem;
    &.disabled {
      padding-left: 4px;
    }
    &.active {
      font-weight: bold;
      background: var(--brand-gradient);
      color: white;
    }
  }
  & .thread-selector {
    margin-top: 0.5rem;
    & .btn-group {
      padding: 0.1rem !important;
    }
    & .btn {
      font-size: 0.75rem;
      padding: 0.2rem 0.5rem;
    }
  }
  .condition & {
    display: flex;
    align-items: center;
    margin: 0 !important;
    .thread-selector {
      margin-top: 0rem;
    }
  }
  .condition-summary & {
    display: flex;
    align-items: center;
    padding: 0 !important;
    margin: 0 !important;
    .thread-selector {
      margin-top: 0rem;
      padding: 0 0.2rem;
    }
  }
`;

interface CompoundValue {
  Type?: string;
  Value?: any;
  ValueFrom?: string;
  Key?: string;
  Order?: number;
  ComponentConfig?: any;
  MainThread?: boolean;
  WithNewThread?: boolean;
  FromParentThread?: boolean;
  [key: string]: any;
}

interface CompoundInputProps {
  isConditionInput?: boolean;
  values: CompoundValue[];
  onChange: (values: CompoundValue[]) => void;
  isSummary?: boolean;
  optionType?: string;
  inputType?: string;
  isMulti?: boolean;
  options?: any[];
  defaultValue?: any;
  showKeyInput?: boolean;
  keyValue?: string;
  showDefaultValueInput?: boolean;
  isStaticOnly?: boolean;
  isRawData?: boolean;
  isInputArgumentOnly?: boolean;
  inputArgumentProps?: any;
  showStaticSelector?: boolean;
  showStaticOptionsSelector?: boolean;
  showInputArgumentSelector?: boolean;
  showApiSelector?: boolean;
  showSolutionRoleSelector?: boolean;
  inputArgumentFilter?: (arg: any) => boolean;
  useDataField?: boolean;
  isValidNewOption?: (a: any) => boolean;
  onCreateOption?: (v: any) => any;
  dateTimeType?: string;
  isDateTimeUTC?: boolean;
  showOperationSelector?: boolean;
  isClearable?: boolean;
  showUnitInput?: boolean;
  showSignInput?: boolean;
  customValueFromOptions?: any[];
  showFilterOptions?: boolean;
  showNewThreadOption?: boolean;
  showParentThreadOption?: boolean;
  disableThreadOption?: boolean;
  filterOptions?: any;
  condition?: any;
  [key: string]: any;
}

/**
 * Props for CompoundInput config objects (e.g. in actionOptionsConfig).
 * Same as CompoundInputProps but without the runtime-provided props:
 * `values`, `onChange`, `isConditionInput`, `condition`, `optionType`, and `defaultValue`.
 */
export type CompoundInputConfigProps = Omit<
  CompoundInputProps,
  | "values"
  | "onChange"
  | "isConditionInput"
  | "condition"
  | "optionType"
  | "defaultValue"
>;

export default function CompoundInput({
  isConditionInput = false,
  values,
  onChange,
  isSummary = false,
  optionType,
  inputType,
  isMulti,
  options,
  defaultValue,
  showKeyInput = false,
  keyValue,
  showDefaultValueInput = false,
  isStaticOnly = false,
  isRawData = false,
  isInputArgumentOnly = false,
  inputArgumentProps,
  showStaticSelector = true,
  showStaticOptionsSelector = false,
  showInputArgumentSelector = true,
  showApiSelector = false,
  showSolutionRoleSelector = false,
  inputArgumentFilter,
  useDataField = false,
  isValidNewOption,
  onCreateOption,
  dateTimeType,
  isDateTimeUTC,
  showOperationSelector = false,
  isClearable = false,
  showUnitInput = false,
  showSignInput = false,
  customValueFromOptions,
  showFilterOptions = false,
  showNewThreadOption = false,
  showParentThreadOption = false,
  disableThreadOption = false,
  filterOptions,
  condition,
  ...props
}: CompoundInputProps) {
  const inputArguments = useSelector(selectInputArguments);
  let inputArgumentOptions = inputArguments;
  if (inputArgumentFilter) {
    inputArgumentOptions = inputArgumentOptions.filter((option: any) =>
      inputArgumentFilter(option)
    );
  } else if (inputArgumentProps) {
    inputArgumentOptions = Object.keys(inputArgumentProps).reduce(
      (acc, key) =>
        acc.filter((option: any) => {
          if (typeof inputArgumentProps[key] === "boolean") {
            return !!option[key] === inputArgumentProps[key];
          }
          return Array.isArray(inputArgumentProps[key])
            ? inputArgumentProps[key].includes(option[key])
            : option[key] === inputArgumentProps[key];
        }),
      inputArgumentOptions
    );
  }
  const dataCategoryOptions = useSelector(selectDataCategories);
  const rawDataCategoryOptions = useSelector(selectRawDataCategories);
  const counterCategoryOptions = useSelector(selectCounterCategories);
  const dispatch = useDispatch();

  const onChangeHandler = (
    f: string,
    i: number,
    v: any,
    valueType = "static"
  ) => {
    const newValues = [...values];

    const newValue = {
      ...newValues[i],
      Type: optionType,
      ...(keyValue ? { Key: keyValue } : {}),
      [f]: v,
      ...(f === "ValueFrom"
        ? {
            ...(v === "inputArgument"
              ? { Value: inputArgumentOptions[0]?.UUID }
              : v === "api"
              ? { Value: "none" }
              : { Value: defaultValue[0]?.Value || defaultValue[0] || "" }),
          }
        : {}),
    };
    if (f === "MainThread") {
      if (v === "new") {
        newValue.MainThread = false; // default to current thread for new thread option, can be changed by user
        newValue.FromParentThread = false;
        newValue.WithNewThread = true; // custom property to indicate this is a new thread option, can be used for special handling if needed
      } else if (v === "parent") {
        newValue.MainThread = false; // default to current thread for new thread option, can be changed by user
        newValue.WithNewThread = false; // custom property to indicate this is a new thread option, can be used for special handling if needed
        newValue.FromParentThread = true;
      } else {
        newValue.MainThread = v;
        newValue.WithNewThread = false;
        newValue.FromParentThread = false;
      }
    }
    if (
      valueType === "inputArgument" ||
      (f === "ValueFrom" && v === "inputArgument") ||
      newValue.ValueFrom === "inputArgument"
    ) {
      newValue.ValueFrom = "inputArgument";
    } else if (
      valueType === "static-options" ||
      (f === "ValueFrom" && v === "static-options") ||
      newValue.ValueFrom === "static-options"
    ) {
      newValue.ValueFrom = "static-options";
      newValue.Value = "";
    } else if (f !== "MainThread") {
      delete newValue.ComponentConfig;
    }
    newValues.splice(i, 1, newValue);
    onChange(newValues);
  };

  return (
    <div>
      <div>
        {!isSummary && (
          <div className="d-flex justify-content-between mb-1">
            <InputLabel>{props.label}</InputLabel>
            {isRawData && (
              <span className="badge badge-pill bg-secondary float-end">
                Raw JSON
              </span>
            )}
            {isMulti && (
              <button
                type="button"
                className="btn btn-outline-secondary border-0 py-0 px-1 ms-2"
                aria-label="Add new value"
                onClick={() =>
                  onChange([
                    ...values,
                    {
                      Type: optionType,
                      ValueFrom: "static",
                      Value: defaultValue || "",
                    },
                  ])
                }
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            )}
          </div>
        )}
        {(values.length > 0 ? values : [{ Value: "" }]).map((value, index) => (
          <StyledInput
            key={index}
            className={`${
              isMulti
                ? "mb-2 p-2 border rounded bg-secondary bg-gradient bg-opacity-10 "
                : ""
            }`}
          >
            {isMulti && (
              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-danger border-0 py-0 px-1"
                  aria-label="Remove value"
                  onClick={() =>
                    onChange([...values.filter((v, i) => i !== index)])
                  }
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>
              </div>
            )}
            {showKeyInput && (
              <div className="d-flex mb-3">
                <InputGroupText className="rounded-end-0">Key</InputGroupText>
                <div className="w-100 rounded-start-0 overflow-hidden">
                  <Input
                    className="ms-n1"
                    value={value?.Key ? [value?.Key] : []}
                    onChange={([v]) => {
                      onChangeHandler("Key", index, v);
                    }}
                  />
                </div>
              </div>
            )}
            <div className="d-flex justify-content-between">
              {!isStaticOnly && !isInputArgumentOnly && (
                <Input
                  inputType={isConditionInput ? "radio" : "tab"}
                  radioName={uuidv4()}
                  options={[
                    { value: "", label: "Value from:", disabled: true },

                    ...(showStaticSelector
                      ? [{ value: "static", label: "Static" }]
                      : []),

                    ...(showStaticOptionsSelector
                      ? [{ value: "static-options", label: "Static options" }]
                      : []),

                    ...(showInputArgumentSelector
                      ? [{ value: "inputArgument", label: "Variable" }]
                      : []),

                    ...(showApiSelector
                      ? [{ value: "api", label: "API" }]
                      : []),

                    ...(customValueFromOptions ? customValueFromOptions : []),
                  ]}
                  value={[value?.ValueFrom ?? "static"]}
                  onChange={([v]) => {
                    onChangeHandler("ValueFrom", index, v);
                  }}
                />
              )}
              <div className="flex-grow-1" />
            </div>
            {(value?.ValueFrom === "static" || isStaticOnly) &&
              !isInputArgumentOnly && (
                <Input
                  value={useDataField ? [value?.Data] : [value?.Value]}
                  onChange={([v]) => {
                    onChangeHandler(useDataField ? "Data" : "Value", index, v);
                  }}
                  inputType={inputType as any}
                  options={options}
                  isValidNewOption={isValidNewOption}
                  onCreateOption={
                    onCreateOption
                      ? ([v]) => {
                          onChangeHandler(
                            useDataField ? "Data" : "Value",
                            index,
                            onCreateOption(v)
                          );
                        }
                      : undefined
                  }
                  maxLength={props.maxLength}
                  type={props.type}
                  min={props.min}
                  max={props.max}
                  dateTimeType={dateTimeType}
                  isDateTimeUTC={isDateTimeUTC}
                  radioName={`${condition.UUID}-${optionType}-static-${index}-${keyValue}`}
                  isSummary={isSummary}
                  contactSetType={props.contactSetType}
                />
              )}

            {(value?.ValueFrom === "inputArgument" || isInputArgumentOnly) && (
              <div className="d-flex">
                <div className="flex-grow-1">
                  <Input
                    isSummary={isSummary}
                    value={value?.Value ? [value?.Value] : []}
                    isClearable={isClearable}
                    onChange={([v]) => {
                      onChangeHandler("Value", index, v, "inputArgument");
                    }}
                    inputType="select"
                    options={inputArgumentOptions.map((option: any) => ({
                      value: option.UUID,
                      label: option.Label,
                    }))}
                    isValidNewOption={isValidNewOption}
                    onCreateOption={(v: any) => {
                      const newIAUUID = uuidv4();
                      dispatch(
                        addNewInputArgument({
                          UUID: newIAUUID,
                          Label: v,
                          ...Object.entries(inputArgumentProps ?? {}).reduce(
                            (acc, [key, val]) => ({
                              ...acc,
                              [key]: Array.isArray(val) ? val[0] : val,
                            }),
                            {}
                          ),
                        }) as any
                      );
                      dispatch(
                        setCurrentModal({
                          modal: "inputArgument",
                          data: { inputArgumentUUID: newIAUUID },
                        })
                      );
                      onChangeHandler(
                        "Value",
                        index,
                        newIAUUID,
                        "inputArgument"
                      );
                    }}
                  />
                </div>
                {value?.Value && !isSummary && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      // Open the variable modal for editing
                      dispatch(
                        setCurrentModal({
                          modal: "inputArgument",
                          data: { inputArgumentUUID: value?.Value },
                        })
                      );
                    }}
                    className="btn btn-link p-0 ms-2"
                  >
                    <FontAwesomeIcon icon={faCog} />
                  </button>
                )}
              </div>
            )}

            {(value?.ValueFrom === "inputArgument" || isInputArgumentOnly) &&
              (isSummary ? (
                <div className="thread-selector">
                  {!!value?.MainThread ? "Main Thread" : "Current Thread"}
                </div>
              ) : (
                <div className="d-flex thread-selector">
                  {!isConditionInput && (
                    <div>
                      {(value?.ValueFrom === "inputArgument" ||
                        isInputArgumentOnly) && (
                        <div>
                          {Object.entries(inputArgumentProps || {}).map(
                            ([key, val]) => (
                              <span
                                className="badge bg-secondary me-2"
                                key={key}
                              >
                                {key}:{" "}
                                {Array.isArray(val)
                                  ? val.join(" | ")
                                  : val?.toString()}
                              </span>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="ms-auto">
                    {showOperationSelector && (
                      <Input
                        value={[value?.Operation ?? "append"]}
                        onChange={([v]) => {
                          onChangeHandler("Operation", index, v);
                        }}
                        inputType="radio"
                        radioName={`${condition.UUID}-${optionType}-VariableOperation-${index}`}
                        options={[
                          { value: "append", label: "Append" },
                          { value: "replace", label: "Replace" },
                          { value: "delete-first", label: "Delete first" },
                        ]}
                      />
                    )}
                  </div>
                  <div className="ms-2">
                    <Input
                      value={
                        value?.WithNewThread
                          ? ["new"]
                          : value?.FromParentThread
                          ? ["parent"]
                          : !!value?.MainThread
                          ? [!!value?.MainThread]
                          : [false]
                      }
                      onChange={([v]) => {
                        onChangeHandler("MainThread", index, v);
                      }}
                      inputType="radio"
                      isDisabled={disableThreadOption}
                      radioName={`${condition.UUID}-${optionType}-dataCategoryThread-${index}-${keyValue}`}
                      options={[
                        { value: true, label: "Main Thread" },
                        { value: false, label: "Current Thread" },
                        ...(showNewThreadOption
                          ? [{ value: "new", label: "New Thread" }]
                          : []),
                        ...(showParentThreadOption
                          ? [{ value: "parent", label: "Parent Thread" }]
                          : []),
                      ]}
                    />
                  </div>
                </div>
              ))}

            {showDefaultValueInput && (
              <Input
                label={`${props.label} Default Value`}
                value={value?.DefaultValue ? [value?.DefaultValue] : []}
                onChange={([v]) => {
                  onChangeHandler("DefaultValue", index, v);
                }}
                inputType={inputType as any}
                options={options}
              />
            )}

            {value?.ValueFrom === "api" && (
              <Input
                value={value?.Value ? [value?.Value] : []}
                onChange={([v]) => {
                  onChangeHandler("Value", index, v);
                }}
                inputType="select"
                options={[
                  { value: "none", label: "None" },
                  { value: "bookings", label: "Nylas Events Calendar" },
                ]}
              />
            )}

            {showUnitInput && (
              <Input
                value={value?.Unit ? [value?.Unit] : []}
                onChange={([v]) => {
                  onChangeHandler("Unit", index, v);
                }}
                inputType="select"
                options={[
                  { value: "second", label: "seconds" },
                  { value: "minute", label: "minutes" },
                  { value: "hour", label: "hours" },
                  { value: "day", label: "days" },
                ]}
              />
            )}

            {showSignInput && (
              <Input
                value={value?.Sign ? [value?.Sign] : []}
                onChange={([v]) => {
                  onChangeHandler("Sign", index, v);
                }}
                inputType="select"
                options={[
                  { value: "positive", label: "Positive (+)" },
                  { value: "negative", label: "Negative (-)" },
                ]}
              />
            )}

            {showSolutionRoleSelector && (
              <Input
                value={value?.SolutionRole ? [value?.SolutionRole] : []}
                onChange={([v]) => {
                  onChangeHandler("SolutionRole", index, v);
                }}
                label="Show in event summary"
                inputType="checkbox"
                options={[
                  { value: "public_reporting", label: "Yes" },
                  { value: "", label: "No" },
                ]}
              />
            )}

            {showFilterOptions && (
              <Input
                key={filterOptions}
                label="Filter operator"
                inputType="radio"
                radioName={`${condition.UUID}-${optionType}-filter-operator-${index}-${keyValue}`}
                value={value?.Filter ? [value?.Filter] : []}
                onChange={([v]) => {
                  onChangeHandler("Filter", index, v);
                }}
                options={
                  filterOptions || [
                    { value: "is", label: "Is" },
                    { value: "is-not", label: "Is not" },
                  ]
                }
              />
            )}
          </StyledInput>
        ))}
      </div>
      {props.html}
    </div>
  );
}
